import { css, html, LitElement, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import { classMap } from "lit/directives/class-map.js";
import { customEvent } from "../../util/event.ts";

// Components
import "../dui/textarea/dui-textarea.ts";
import "../dui/button/dui-button.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/toolbar/dui-toolbar.ts";
import "../dui/menu/dui-menu.ts";
import "../dui/menu/dui-menu-item.ts";
import "../dui/tooltip/dui-tooltip.ts";
import "../dui/tooltip/dui-tooltip-trigger.ts";
import "../dui/tooltip/dui-tooltip-popup.ts";
import { activateCanvasViewEvent } from "./canvas-view-card.ts";

/** Check if a string is empty or whitespace only */
const isEmpty = (value: string): boolean => value.trim().length === 0;

const styles = css`
  .ai-box {
    background-color: var(--card);
    border-radius: var(--radius-lg);
    display: flex;
    flex-direction: column;
    padding: var(--space-4);
    gap: var(--space-2);
    box-shadow:
      var(--shadow-lg),
      inset 0 0 0 var(--border-width-thin) var(--border);
    transition: box-shadow var(--focus-ring-duration) var(--focus-ring-easing);
  }

  .ai-box:focus-within {
    box-shadow:
      var(--shadow-lg),
      inset 0 0 0 var(--focus-ring-width) var(--focus-ring-color);
  }

  :host([inset]) .ai-box {
    margin: 0 var(--space-4) var(--space-4);
  }

  dui-textarea {
    --font-size: var(--font-size-md);
  }
`;

export const submitInput = customEvent<string>("submit-input", {
  bubbles: true,
  composed: true,
});

@customElement("ai-box")
export class AiBoxElement extends LitElement {
  static override styles = [Styles.base, styles];

  @property({ type: Boolean })
  accessor disabled: boolean = false;

  @property({ type: String })
  accessor value: string = "";

  @property({ type: String })
  accessor placeholder: string = "";

  @property({ attribute: "focus-token" })
  accessor focusToken: string | undefined = undefined;

  #focusPending = false;

  override updated(changed: PropertyValues): void {
    if (changed.has("focusToken") && this.focusToken !== undefined) {
      this.#focusPending = true;
    }
    if (this.#focusPending && !this.disabled) {
      this.#focusPending = false;
      requestAnimationFrame(() => this.focus());
    }
  }

  get #isSubmitDisabled() {
    return this.disabled || isEmpty(this.value);
  }

  override focus(): void {
    this.shadowRoot?.querySelector("dui-textarea")?.focus();
  }

  #onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Enter" && event.shiftKey === false) {
      event.preventDefault();
      if (!this.#isSubmitDisabled) {
        this.dispatchEvent(submitInput(this.value));
        this.value = "";
      }
    }
  };

  #onSubmit = () => {
    if (!this.#isSubmitDisabled) {
      this.dispatchEvent(submitInput(this.value));
      this.value = "";
    }
  };

  #onInput = (event: CustomEvent<{ value: string }>) => {
    this.value = event.detail.value;
  };

  #onActivateCanvasView = (view: string) => () => {
    this.dispatchEvent(activateCanvasViewEvent(view));
  };

  override render() {
    const aiBoxClasses = classMap({
      "ai-box": true,
      disabled: this.disabled,
    });

    return html`
      <div class="${aiBoxClasses}">
        <dui-textarea
          variant="ghost"
          .value="${this.value}"
          .placeholder="${this.placeholder}"
          ?disabled="${this.disabled}"
          resize="auto"
          max-height="var(--space-56)"
          @textarea-change="${this.#onInput}"
          @keydown="${this.#onKeydown}"
        ></dui-textarea>
        <dui-toolbar>
          <dui-tooltip slot="left" delay="250" side="bottom">
            <dui-tooltip-trigger>
              <dui-menu>
                <dui-button
                  slot="trigger"
                  variant="ghost"
                  square
                  aria-label="Attach"
                >
                  <dui-icon icon="add"></dui-icon>
                </dui-button>
                <dui-menu-item disabled>Add files</dui-menu-item>
                <dui-menu-item disabled>Add photos</dui-menu-item>
              </dui-menu>
            </dui-tooltip-trigger>
            <dui-tooltip-popup .showArrow="${false}">Attach</dui-tooltip-popup>
          </dui-tooltip>
          <dui-tooltip slot="left" delay="250" side="bottom">
            <dui-tooltip-trigger>
              <dui-menu>
                <dui-button
                  slot="trigger"
                  variant="ghost"
                  square
                  aria-label="Tools"
                >
                  <dui-icon icon="construction"></dui-icon>
                </dui-button>
                <dui-menu-item
                  @click="${this.#onActivateCanvasView("force-table")}"
                  >Force Table</dui-menu-item
                >
                <dui-menu-item
                  @click="${this.#onActivateCanvasView("force-vis")}"
                  >Force Visualization</dui-menu-item
                >
                <dui-menu-item
                  @click="${this.#onActivateCanvasView("project-info")}"
                  >Project Info</dui-menu-item
                >
              </dui-menu>
            </dui-tooltip-trigger>
            <dui-tooltip-popup .showArrow="${false}">Tools</dui-tooltip-popup>
          </dui-tooltip>
          <dui-tooltip slot="right">
            <dui-tooltip-trigger>
              <dui-button
                variant="primary"
                square
                aria-label="Send"
                ?disabled="${this.#isSubmitDisabled}"
                @click="${this.#onSubmit}"
                ><dui-icon icon="arrow_upward"></dui-icon
              ></dui-button>
            </dui-tooltip-trigger>
            <dui-tooltip-popup .showArrow="${false}">Send</dui-tooltip-popup>
          </dui-tooltip>
        </dui-toolbar>
      </div>
    `;
  }
}
